import React from 'react';
import { Plus, X, MinusCircle, Search } from '../../../bravosIcons';
import './library-empty-state.css';

/**
 * Trainer Library — three-variant empty state component (Item 4 of
 * `_bmad-output/planning-artifacts/trainer-library-ui-improvements-dev-handoff.md`).
 *
 * Trigger precedence (evaluated by caller, in this exact order):
 *   1. `true-empty`               — Workouts / Workout Plans tab AND the raw list (pre-filter) is empty.
 *   2. `filtered-empty-exercises` — Exercises tab AND `Created by: You` is the only Created-by filter active AND filtered list is empty.
 *   3. `no-match`                 — Filtered+searched list is empty AND state 1 & 2 don't apply.
 *
 * Variants differ in layout:
 *   - `true-empty` and `filtered-empty-exercises` use the 2-column zero-state
 *     grid (left: copy + CTA, right: skeleton preview cards).
 *   - `no-match` is centered with a circular search-minus icon, headline,
 *     accent-highlighted search term, and Clear / Create CTAs.
 *
 * Visual reference: `_bmad-output/design-references/library-ui-improvements/`
 *   - `library-workouts-zero.html`             — workouts true-empty
 *   - `library-workout-plans-zero.html`        — plans true-empty
 *   - `library-exercises-zero.html`            — exercises filtered-empty
 *   - `library-workouts-02-selection.html`     — no-match block + CSS (lines 652-735)
 */
export type LibraryEmptyStateVariant =
  | 'true-empty'
  | 'filtered-empty-exercises'
  | 'no-match';

export type LibraryEmptyStateTab = 'workouts' | 'workoutPlans' | 'exercises';

export type LibraryEmptyStateProps = {
  variant: LibraryEmptyStateVariant;
  tab: LibraryEmptyStateTab;
  /** Current search input; used by `no-match` body copy with accent highlight. */
  searchQuery: string;
  /** True when 1+ filters / sort-non-default is applied. Drives the
   *  `Clear filters & search` button visibility on the `no-match` state. */
  hasActiveFilters: boolean;
  /** Resets filters + search + sort to per-tab defaults. Wired by caller. */
  onClearAll: () => void;
  /** Tab's create-new entry point (`Create workout`, `Create plan`, `Create exercise`). */
  onCreate: () => void;
};

const tabCopy: Record<LibraryEmptyStateTab, {
  eyebrow: string;
  titlePrefix: string;
  titleEm: string;
  subtitle: string;
  createLabel: string;
  previewLabel: string;
  itemPlural: string;
}> = {
  workouts: {
    eyebrow: 'Your library starts here',
    titlePrefix: 'Build your first',
    titleEm: ' workout',
    subtitle:
      'Workouts are the building blocks of your library. Combine exercises, sets, and prescriptions — then reuse them across clients and plans.',
    createLabel: 'Create workout',
    previewLabel: 'A workout looks like',
    itemPlural: 'workouts',
  },
  workoutPlans: {
    eyebrow: 'Programming starts here',
    titlePrefix: 'Design a',
    titleEm: ' plan',
    subtitle:
      'Plans organise workouts across weeks — a coherent block of training with a clear goal. Build one, then assign it to clients in a few clicks.',
    createLabel: 'Create plan',
    previewLabel: 'A plan looks like',
    itemPlural: 'workout plans',
  },
  exercises: {
    eyebrow: 'Make the library yours',
    titlePrefix: 'Add your own',
    titleEm: ' exercises',
    subtitle:
      'Bravos ships with ready to use exercises — but the ones unique to your coaching live here. Custom variations, tempo prescriptions or movements specific to your gym.',
    createLabel: 'Create exercise',
    previewLabel: 'Your exercises will look like',
    itemPlural: 'exercises',
  },
};

/** Decorative skeleton preview cards used on `true-empty` + `filtered-empty-exercises`.
 *  Pure layout; never bind data here. `aria-hidden` on the wrapper. */
const SkeletonPreviewCards: React.FC<{ label: string }> = ({ label }) => (
  <div className="lib-zero-preview" aria-hidden="true">
    <div className="lib-preview-label">{label}</div>
    <div className="lib-preview-card lib-preview-card--alt-1">
      <span className="lib-preview-num">01</span>
      <div className="lib-preview-body">
        <span className="lib-preview-title-line" />
        <div className="lib-preview-pill-row">
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--light" />
        </div>
      </div>
    </div>
    <div className="lib-preview-card lib-preview-card--alt-2">
      <span className="lib-preview-num">02</span>
      <div className="lib-preview-body">
        <span className="lib-preview-title-line" />
        <div className="lib-preview-pill-row">
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--light" />
        </div>
      </div>
    </div>
    <div className="lib-preview-card lib-preview-card--alt-3">
      <span className="lib-preview-num">03</span>
      <div className="lib-preview-body">
        <span className="lib-preview-title-line" />
        <div className="lib-preview-pill-row">
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--dark" />
          <span className="lib-preview-pill lib-preview-pill--light" />
        </div>
      </div>
    </div>
  </div>
);

const LibraryEmptyState: React.FC<LibraryEmptyStateProps> = ({
  variant,
  tab,
  searchQuery,
  hasActiveFilters,
  onClearAll,
  onCreate,
}) => {
  const copy = tabCopy[tab];

  if (variant === 'no-match') {
    /* `no-match` design uses a centred single-column layout — separate path
     * from the 2-column zero-state grid. */
    const trimmedQuery = searchQuery.trim();
    return (
      <div className="lib-no-match" role="status" aria-live="polite">
        <div className="lib-no-match__eyebrow">— No matches —</div>
        <div className="lib-no-match__icon" aria-hidden="true">
          <MinusCircle />
          <Search className="lib-no-match__icon-overlay" />
        </div>
        <h3 className="lib-no-match__title">Nothing here — yet</h3>
        <p className="lib-no-match__sub">
          {trimmedQuery ? (
            <>
              No {copy.itemPlural} match{' '}
              <mark className="lib-no-match__query">&ldquo;{trimmedQuery}&rdquo;</mark>{' '}
              with the active filters. Try a different search, clear filters, or build something new.
            </>
          ) : (
            <>No {copy.itemPlural} match the active filters. Try clearing filters, or build something new.</>
          )}
        </p>
        <div className="lib-no-match__actions">
          {hasActiveFilters && (
            <button
              type="button"
              className="lib-no-match__btn-ghost"
              onClick={onClearAll}
            >
              <X aria-hidden="true" />
              Clear filters &amp; search
            </button>
          )}
          <button
            type="button"
            className="lib-no-match__btn-link"
            onClick={onCreate}
          >
            {copy.createLabel} <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    );
  }

  /* `true-empty` and `filtered-empty-exercises` share the same 2-column grid. */
  return (
    <div className="lib-zero-state" data-variant={variant}>
      <div className="lib-zero-copy">
        <div className="lib-zero-eyebrow">
          <span className="lib-zero-eyebrow__dot" aria-hidden="true" />
          {copy.eyebrow}
        </div>
        <h2 className="lib-zero-title">
          {copy.titlePrefix}
          {copy.titleEm}
        </h2>
        <p className="lib-zero-sub">{copy.subtitle}</p>
        <div className="lib-zero-actions">
          <button
            type="button"
            className="lib-zero-btn-primary"
            onClick={onCreate}
          >
            <Plus aria-hidden="true" />
            {copy.createLabel}
          </button>
        </div>
      </div>
      <SkeletonPreviewCards label={copy.previewLabel} />
    </div>
  );
};

export default LibraryEmptyState;
